import { Box, CircularProgress, Drawer, Typography } from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { $api } from 'shared/api/api';
import { PropertyCatalogItemDetailed } from 'shared/ui/PropertyCatalogItemDetailed/PropertyCatalogItemDetailed';

import type {
    CatalogItem,
    DisplayItem,
} from 'entities/CatalogItem/model/types/CatalogItemSchema';
import type { ExtendedAxiosRequestConfig } from 'shared/api/types';

interface SavingsSummaryProductDetailsDrawerProps {
    sku: string | null;
    open: boolean;
    onClose: () => void;
}

export const SavingsSummaryProductDetailsDrawer = memo(
    ({ sku, open, onClose }: SavingsSummaryProductDetailsDrawerProps) => {
        const { t } = useTranslation();
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState<string | null>(null);
        const [item, setItem] = useState<CatalogItem | null>(null);

        useEffect(() => {
            if (!open || !sku) {
                return;
            }

            let isCancelled = false;

            const fetchItem = async () => {
                setLoading(true);
                setError(null);
                try {
                    const config: ExtendedAxiosRequestConfig = {
                        keysToNotConvert: ['specifications'],
                    };
                    const response = await $api.get<CatalogItem>(
                        `/v1/catalogs/items/${encodeURIComponent(sku)}`,
                        config,
                    );
                    if (!isCancelled) {
                        setItem(response.data as CatalogItem);
                    }
                } catch {
                    if (!isCancelled) {
                        setError('failed');
                    }
                } finally {
                    if (!isCancelled) {
                        setLoading(false);
                    }
                }
            };

            fetchItem();

            return () => {
                isCancelled = true;
            };
        }, [open, sku]);

        const displayItem: DisplayItem | null = useMemo(() => {
            if (!item) return null;

            const maybeGlobalCategory: any = (item as any).category;
            let category = '';
            if (
                maybeGlobalCategory &&
                typeof maybeGlobalCategory === 'object'
            ) {
                const parent = maybeGlobalCategory.parentCategory;
                if (parent?.catName) {
                    category = `${parent.catName} / ${maybeGlobalCategory.catName}`;
                } else if (maybeGlobalCategory.catName) {
                    category = maybeGlobalCategory.catName;
                }
            }

            return {
                name: item.title,
                category,
                sku: item.sku,
                uom: item.uom,
                qtyPerUom: item.qtyPerUom,
                uomPrice: item.uomPrice,
                imageUrl: item.imageUrl,
                thumbnailUrl: item.thumbnailUrl,
                manufacturerId: item.manufacturerId,
                mpn: item.mpn,
                upc: item.upc,
                brand: item.brand,
                msrp: item.msrp,
                unspsc: item.unspsc,
                description: item.description,
                descriptionFormat: item.descriptionFormat,
                prop65Warning: item.prop65Warning,
                specifications: item.specifications,
                additionalImages: item.additionalImages,
                proposals: item.proposals,
                glCode: item.glCode,
            };
        }, [item]);

        const handleClose = () => {
            onClose();
        };

        return (
            <Drawer
                anchor="right"
                open={open}
                onClose={handleClose}
                sx={{
                    zIndex: (theme) => theme.zIndex.modal + 1,
                }}
                PaperProps={{
                    sx: {
                        width: 480,
                        maxWidth: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100vh',
                    },
                }}
                ModalProps={{
                    BackdropProps: {
                        invisible: true,
                    },
                }}
            >
                <Box sx={{ flex: 1, padding: 2, minHeight: 0 }}>
                    {loading && (
                        <Box
                            sx={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    {!loading && error && (
                        <Typography color="error" variant="body2">
                            {t('Unknown Error')}
                        </Typography>
                    )}
                    {!loading && !error && displayItem && (
                        <PropertyCatalogItemDetailed
                            displayItem={displayItem}
                            onCancel={handleClose}
                            price={item?.price ?? 0}
                            id={item?.id ?? sku ?? ''}
                            isOrderPage={false}
                            moveTo={undefined}
                            minAllowedInStock={0}
                            isLocal={Boolean(item?.id)}
                            hideMinOnHand
                            headerMode="close-only"
                        />
                    )}
                </Box>
            </Drawer>
        );
    },
);

SavingsSummaryProductDetailsDrawer.displayName =
    'SavingsSummaryProductDetailsDrawer';
